import {useCallback, useEffect, useState} from 'react';
import {useIsMounted} from '../../../../../../common/shared/hooks/useIsMounted';
import {
  getStaticAssetUrl,
  StaticAssetType,
} from '../../../../../../common/shared/utils/getStaticAssetUrl';
import {TEXT_VIEWER_FONT_FAMILY_FILE_NAME} from '../constant';
import {TextViewerFontFamilyType} from '../type/TextViewerFontFamilyType.enum';

/**
 * 필요한 폰트가 전부 로딩되었는지 확인합니다.
 *
 * 아직 로딩 전인 경우 null, 로드가 되었으면 true, timeout 밀리초까지 로드를 실패하면 false를 반환합니다.
 * 카이가 만든 TalkViewerGlobalStyleContainer.tsx 파일을 참고했습니다.
 */
export function useAllFontsLoaded({
  fontList,
  timeoutMs = 3000,
  loaded = null,
}: {
  fontList: TextViewerFontFamilyType[];
  timeoutMs?: number;
  loaded?: boolean | null;
}) {
  const [isLoaded, setIsLoaded] = useState<boolean | null>(loaded);

  const getFontUrl = useCallback(
    (fontName: string) => getStaticAssetUrl(fontName, StaticAssetType.Font),
    []
  );

  const isMounted = useIsMounted();

  useEffect(() => {
    if (isLoaded !== null) {
      return;
    }

    // draft인 fontface API를 사용해서 폰트가 로드되었는지 확인합니다.
    const loadAllFontPromise = Promise.all(
      fontList.map(font => {
        return new Promise<void>((resolve, reject) => {
          try {
            const fontName = font as TextViewerFontFamilyType;
            const url = getFontUrl(TEXT_VIEWER_FONT_FAMILY_FILE_NAME[font]);
            if (!('FontFace' in window)) {
              reject();
            }
            const fontFace = new FontFace(fontName, `url(${url})`);
            fontFace.load().then(() => {
              document.fonts?.add(fontFace);
              resolve();
            });
          } catch {
            reject();
          }
        });
      })
    );

    // timeout 값까지 폰트를 모두 로딩하지 못하면 정상적이지 못한 상황으로 가정하고 promise를 reject 합니다.
    const timeoutFallbackPromise = new Promise((_, reject) => {
      setTimeout(reject, timeoutMs);
    });

    Promise.race([loadAllFontPromise, timeoutFallbackPromise])
      .then(() => {
        if (isMounted()) {
          setIsLoaded(true);
        }
      })
      .catch(() => {
        if (isMounted()) {
          setIsLoaded(false);
        }
      });
  }, [fontList, getFontUrl, isLoaded, isMounted, timeoutMs]);

  return isLoaded;
}
